import { PiecewiseFunction } from '@fullstory/piecewise-function';
import { PageModel } from '../models/Page';
import { SessionModel } from '../models/Session';
import { SessionExpr } from './sessionexpr';

// TODO(jordanhurt): move to parent component
export const RANGEKIND_PLAY = 0; // Playing
export const RANGEKIND_DEAD = 1; // Deadspace
export const RANGEKIND_SKIP = 2; // Playback skip
export const RANGEKIND_GAP = 3; // Gap in playback data (between pages)
export type RANGEKIND =
  | typeof RANGEKIND_PLAY
  | typeof RANGEKIND_DEAD
  | typeof RANGEKIND_SKIP
  | typeof RANGEKIND_GAP;

export const RANGEKIND_PRIORITY: { [k in RANGEKIND]: number } = {
  [RANGEKIND_GAP]: 0,
  [RANGEKIND_DEAD]: 1,
  [RANGEKIND_PLAY]: 2,
  [RANGEKIND_SKIP]: 3,
};

export interface TimeRange {
  /**
   * The type of range: PLAY | DEAD | GAP | SKIP
   */
  kind: RANGEKIND;
  /**
   * The priority of this range relative to others
   */
  priority: number;
  /**
   * The absolute start time in ms.
   */
  start: number;
  /**
   * The absolute end time in ms.
   */
  end: number;
}

/**
 * @deprecated
 */
export interface LegacyTimeRange extends TimeRange {
  /**
   * @deprecated
   */
  size: number; // 0-1
  /**
   * @deprecated
   */
  absDelta: number; // amount by which the true absolute time has been adjusted
  /**
   * @deprecated
   */
  page: Readonly<PageModel> | null; // optional pointer to the underlying page
}

export enum SearchDirection {
  Forward = 1,
  Backward = -1,
}

interface FindRangeAtTimeOptions {
  /**
   * Prevents ranges with matching kinds from being returned
   */
  ignoreRangeKinds?: RANGEKIND[];
  /**
   * Ignores ranges with zero length (where range.start === range.end)
   */
  ignoreZeroLength?: boolean;
  /**
   * Callback to invoke when a fallback range is returned
   */
  onFallback?: () => void;
  /**
   * Determines if a fallback should be returned when no matching range is found.
   * When `searchDirection === Forward`, fallback will be the last range in the array.
   * When `searchDirection === Backward`, fallback will be the first range in the array.
   * When `ranges.length === 0`, fallback will be `undefined`.
   */
  returnFallback?: boolean;
  /**
   * Determines if searching is performed from start to end or end to start.
   * When `Forward`, finds the first range where `range.end > absTimeMs`.
   * When `Backward`, finds the last range where `range.start <= absTimeMs`.
   * Note: Results are inclusive when `Backward` is `true`.
   */
  searchDirection?: SearchDirection;
}

export function findRangeAtTime<T extends Pick<TimeRange, 'kind' | 'start' | 'end'>>(
  ranges: T[],
  absTimeMs: number,
  options: FindRangeAtTimeOptions = {},
): T | undefined {
  const {
    onFallback = () => {},
    ignoreRangeKinds = [],
    ignoreZeroLength = false,
    returnFallback = false,
    searchDirection = SearchDirection.Forward,
  } = options;

  if (ranges.length === 0) {
    return undefined;
  }

  let cursor = searchDirection === SearchDirection.Forward ? 0 : ranges.length - 1;
  const updateCursor = () => (searchDirection === SearchDirection.Forward ? cursor++ : cursor--);
  const isCursorInBounds = () =>
    searchDirection === SearchDirection.Forward ? cursor < ranges.length : cursor >= 0;

  const isRangeIgnored = (range: T) => ignoreRangeKinds.includes(range.kind);
  const isRangeZeroLength = (range: T) => range.start === range.end;
  const isRangeTimeMatch = (range: T) =>
    searchDirection === SearchDirection.Forward ? range.end > absTimeMs : range.start <= absTimeMs;

  while (isCursorInBounds()) {
    const range = ranges[cursor];
    updateCursor();

    if (!range) {
      continue;
    }

    if (ignoreRangeKinds.length && isRangeIgnored(range)) {
      continue;
    }

    if (ignoreZeroLength && isRangeZeroLength(range)) {
      continue;
    }

    if (isRangeTimeMatch(range)) {
      return range;
    }
  }

  if (returnFallback) {
    onFallback?.();
    return searchDirection === SearchDirection.Forward ? ranges[ranges.length - 1] : ranges[0];
  }

  return undefined;
}

export function computeTimeRanges(session: SessionModel, sessionExpr: SessionExpr) {
  const pages = session.pages;

  if (pages.length === 0) {
    return [];
  }

  const start = Math.min(...pages.map(p => p.localStartTime));
  const end = Math.max(...pages.map(p => p.localEndTime));
  const rangesPwf = new PiecewiseFunction<Pick<LegacyTimeRange, 'kind' | 'page'>>();

  // Add a RANGEKIND_GAP with the lowest priority to fill any unused space between pages
  rangesPwf.add(
    {
      kind: RANGEKIND_GAP,
      page: null,
    },
    start,
    end,
    RANGEKIND_PRIORITY[RANGEKIND_GAP],
  );

  // Add ranges for each page
  for (const page of pages) {
    // Add a RANGEKIND_DEAD with lower priority to fill any unused space in the page
    if (!sessionExpr.isClip) {
      // clips do not have dead spaces
      rangesPwf.add(
        { kind: RANGEKIND_DEAD, page },
        page.localStartTime,
        page.localEndTime,
        RANGEKIND_PRIORITY[RANGEKIND_DEAD],
      );
    }

    // Add a higher priority RANGEKIND_PLAY for every active section
    const activeSpace = getActiveSpace(page);

    for (const activity of activeSpace) {
      rangesPwf.add(
        {
          kind: RANGEKIND_PLAY,
          page,
        },
        activity.start,
        activity.end,
        RANGEKIND_PRIORITY[RANGEKIND_PLAY],
      );
    }
  }

  if (sessionExpr.isClip) {
    // clips have skipped content that are not include with the given playback session url
    const skipedIntervals = getSkippedIntervals(sessionExpr, start, end);
    for (const interval of skipedIntervals) {
      rangesPwf.add(
        {
          kind: RANGEKIND_SKIP,
          page: null,
        },
        interval.start,
        interval.end,
        RANGEKIND_PRIORITY[RANGEKIND_SKIP],
      );
    }
  }

  // Populate ranges with the newly computed timeline
  const results: LegacyTimeRange[] = [];

  for (const { subfunction, start, end } of rangesPwf) {
    if (subfunction.kind !== 'constant' || !subfunction.value) continue;

    results.push({
      absDelta: 0,
      kind: subfunction.value.kind,
      priority: RANGEKIND_PRIORITY[subfunction.value.kind],
      start,
      end,
      size: 0,
      page: subfunction.value.page ?? null,
    });
  }

  return results;
}

function getActiveSpace(page: PageModel): Pick<TimeRange, 'start' | 'end' | 'kind'>[] {
  const ranges = new PiecewiseFunction<RANGEKIND>();

  ranges.add(RANGEKIND_PLAY, page.localStartTime, page.localStartTime + page.duration, 1);

  for (const ds of page.deadSpace) {
    ranges.add(RANGEKIND_DEAD, ds.Start + page.localStartTime, ds.End + page.localStartTime, 2);
  }

  const result: Pick<TimeRange, 'kind' | 'start' | 'end'>[] = [];

  for (const range of ranges) {
    if (range.subfunction.kind === 'constant' && range.subfunction.value === RANGEKIND_PLAY) {
      result.push({
        kind: range.subfunction.value,
        start: range.start,
        end: range.end,
      });
    }
  }

  return result;
}

function getSkippedIntervals(
  sessionExpr: SessionExpr,
  start: number,
  end: number,
): { start: number; end: number }[] {
  const timeRanges = new PiecewiseFunction<Pick<TimeRange, 'kind'>>();
  const LEAD_TRAIL_TIME = 6000; // How much time to add before/after markers.

  if (!sessionExpr.isClip) {
    // sanity check
    return [];
  }

  timeRanges.add({ kind: RANGEKIND_SKIP }, start, end, 0);
  const times = sessionExpr.times();
  for (const time of times) {
    const clipStart = time - LEAD_TRAIL_TIME;
    const clipEnd = time + LEAD_TRAIL_TIME;
    timeRanges.add(
      { kind: RANGEKIND_PLAY },
      clipStart < start ? start : clipStart,
      clipEnd > end ? end : clipEnd,
      1,
    );
  }

  return timeRanges
    ._getPieces()
    .filter(
      p => p.subfunction.kind === 'constant' && p.subfunction?.value?.kind === RANGEKIND_SKIP,
    );
}
